import React, { useMemo } from 'react';
import { Info } from 'lucide-react';
import { useSAData } from './useSAData';
import { SAKpis } from './SAKpis';
import { Loading } from './Loading';
import { BarList } from './SACharts';
import { ServerTable } from './ServerTable';
import { PRM_COLS } from './columns';
import { pct } from '@/components/po-grn/format';
import { F } from './formulas';

const KPIS = [
  { key: 'prm', label: 'Outward Margin Value (Retailer at MRP)', type: 'money', note: F.outwardValue },
  { key: 'prm_pct', label: 'Outward Margin %', type: 'pct', note: F.outward },
  { key: 'prm_per_unit', label: 'Outward Margin per Unit', type: 'money', note: 'Σ (MRP − SP) × Qty ÷ Σ Qty' },
  { key: 'avg_discount_pct', label: 'Average MRP Discount %', type: 'pct', note: F.discount },
  { key: 'stores_zero_neg', label: 'Stores with Zero/Negative Outward Margin', warn: true, drill: { patch: { flag: ['sales', 'zero_prm'] } } },
  { key: 'products_missing', label: 'Products Missing MRP or Selling Price', warn: true, drill: { patch: { flag: ['sales', 'missing_mrp_sp'] } } },
  { key: 'products_batch_vs_master', label: 'Products: Batch MRP ≠ Current MRP', warn: true, drill: { patch: { flag: ['sales', 'mrp_diff'] } } },
  { key: 'lines_valid', label: 'Lines with Valid MRP & Price', note: (d) => `of ${d.lines_total} sales lines` },
];

const ProductList = ({ title, rows, onPick, testId }) => (
  <div className="rounded-xl border border-orange-200/70 bg-white p-3" data-testid={testId}>
    <div className="text-xs font-semibold text-[#3b2416] mb-2">{title}</div>
    {(rows || []).map((g) => (
      <button key={g.key} type="button" onClick={() => onPick(g)} className="w-full flex justify-between gap-2 text-[11px] py-1 px-1 rounded hover:bg-orange-50" data-testid={`${testId}-item`}>
        <span className="truncate text-stone-700">{g.key} · {g.name}</span>
        <span className={`tabular-nums font-semibold ${g.prm_pct < 0 ? 'text-red-600' : 'text-[#3b2416]'}`}>{pct(g.prm_pct)}</span>
      </button>
    ))}
    {!rows?.length && <div className="text-[11px] text-stone-400 py-3">No products</div>}
  </div>
);

export const RetailerTab = ({ params, onDrill }) => {
  const { data, loading } = useSAData('/retailer-margin', params);
  const txnParams = useMemo(() => ({ ...params, flag: [params.flag, 'sales'].filter(Boolean).join('|') }), [params]);
  const prod = (g) => onDrill({ patch: { skus: [g.key], flag: ['sales'] } }, `Product ${g.key}`);
  return (
    <div className="space-y-4 relative">
      <Loading on={loading} />
      <div className="flex gap-2 rounded-lg border border-orange-200 bg-orange-50 px-4 py-2 text-[11px] text-[#3b2416]" data-testid="sa-prm-disclaimer">
        <Info size={14} className="shrink-0 mt-0.5 text-orange-600" />
        <span><b>Outward Margin</b> (retailer margin at MRP) = MRP − Selling Price, where Selling Price is the GST-inclusive price the store pays OLTOWNS.
          <b> Outward Margin %</b> = {F.outward}. <b>Retailer Markup %</b> = {F.markup} — a different metric. Indicative only (if the store sells at MRP),
          not actual retailer profit. Lines with missing MRP or price are excluded, never treated as zero.</span>
      </div>
      <SAKpis items={KPIS} data={data?.kpis} onDrill={onDrill} testPrefix="sa-prm" />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        <ProductList title="Highest Outward Margin % Products" rows={data?.top_products} onPick={prod} testId="sa-prm-top-products" />
        <ProductList title="Lowest Outward Margin % Products" rows={data?.bottom_products} onPick={prod} testId="sa-prm-bottom-products" />
        <BarList title="Stores with Highest Outward Margin Value" data={data?.top_stores || []} valueKey="prm" testId="sa-prm-top-stores" className="xl:col-span-2"
          onPick={(s) => onDrill({ patch: { franchise_ids: [s.key], flag: ['sales'] } }, `Store ${s.name}`)} />
      </div>
      <ServerTable endpoint="/transactions" params={txnParams} columns={PRM_COLS} testId="sa-prm-table" defaultSort="prm_value" />
    </div>
  );
};
